import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../contexts/AuthContext';
import { getCommissionRecords } from '../../lib/api';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(amount || 0),
  );

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const chipClass = (status) => (
  status === 'paid'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : status === 'approved'
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-amber-50 text-amber-700 border-amber-200'
);

const normalizeRecord = (item) => ({
  id: item.id,
  loanId: item.loan_application_id,
  bank: item.bank_name || '—',
  amount: Number(item.amount || 0),
  status: item.status,
  createdAt: item.created_at,
  paidAt: item.paid_at,
  rate: Number(item.applied_rate || 0),
  calculationName: item.calculation_name,
});

export default function EmployeeCommission() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, paid: 0, pending: 0, approved: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const [allResp, paidResp, pendingResp, approvedResp] = await Promise.all([
          getCommissionRecords(token),
          getCommissionRecords(token, { status: 'paid' }),
          getCommissionRecords(token, { status: 'pending' }),
          getCommissionRecords(token, { status: 'approved' }),
        ]);
        const items = Array.isArray(allResp?.data?.items) ? allResp.data.items : [];
        const paidItems = Array.isArray(paidResp?.data?.items) ? paidResp.data.items : [];
        const pendingItems = Array.isArray(pendingResp?.data?.items) ? pendingResp.data.items : [];
        const approvedItems = Array.isArray(approvedResp?.data?.items) ? approvedResp.data.items : [];
        if (active) {
          setRows(items.map(normalizeRecord));
          setTotals({
            total: items.reduce((sum, item) => sum + Number(item.amount || 0), 0),
            paid: paidItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
            pending: pendingItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
            approved: approvedItems.reduce((sum, item) => sum + Number(item.amount || 0), 0),
          });
        }
      } catch (err) {
        if (!active) return;
        setRows([]);
        setTotals({ total: 0, paid: 0, pending: 0, approved: 0 });
        setError(err?.message || 'Failed to load commission data');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <div className="space-y-6" data-testid="employee-commission">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">My Commission</h1>
        <p className="text-muted-foreground mt-1">Your commission entries generated from disbursed files.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Earned</p>
            <p className="text-2xl font-bold">{formatCurrency(totals.total)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Paid</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totals.paid)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(totals.pending)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Approved</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.approved)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Commission Ledger</CardTitle>
          <CardDescription>Role-scoped commission history.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loan</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Calc</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    Loading commission entries...
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && error ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    {error}
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && !error && rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-sm">{row.loanId}</TableCell>
                  <TableCell>{row.bank}</TableCell>
                  <TableCell className="capitalize">{row.calculationName}</TableCell>
                  <TableCell>{row.calculationName === 'flat' ? formatCurrency(row.rate) : `${row.rate}%`}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(row.amount)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={chipClass(row.status)}>
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(row.paidAt || row.createdAt)}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No commission entries found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
